import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ChartLineUp, SquaresFour, Tag, Sun, Moon, Stack, UsersThree, SignOut } from "@phosphor-icons/react";
import { Toaster } from "sonner";
import { useAuth } from "../context/AuthContext";

const navItems = [
  { to: "/", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" },
  { to: "/templates", label: "Plantillas", icon: Stack, testid: "nav-templates" },
  { to: "/categories", label: "Categorías", icon: Tag, testid: "nav-categories" },
];

const adminItem = { to: "/users", label: "Usuarios", icon: UsersThree, testid: "nav-users" };

export default function Layout() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const items = user?.role === "admin" ? [...navItems, adminItem] : navItems;
  const [dark, setDark] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem("theme") === "dark" : false
  );

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-60 shrink-0 border-r border-border bg-card hidden md:flex flex-col">
        <div className="h-16 flex items-center px-5 border-b border-border">
          <ChartLineUp size={22} weight="duotone" className="text-primary" />
          <span className="ml-2 font-heading font-extrabold tracking-tight text-base">
            CASHFLOW<span className="text-emerald-600 dark:text-emerald-400">.</span>
          </span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          <div className="label-xs text-muted-foreground px-2 pb-2">Workspace</div>
          {items.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`sidebar-link flex items-center gap-3 px-3 py-2 text-sm rounded-sm ${
                  active
                    ? "bg-primary text-primary-foreground font-semibold"
                    : "text-foreground hover:bg-muted"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border">
          <button
            data-testid="theme-toggle-btn"
            onClick={() => setDark((d) => !d)}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-sm hover:bg-muted transition-colors"
          >
            {dark ? <Sun size={18} weight="fill" /> : <Moon size={18} weight="fill" />}
            <span>{dark ? "Modo Claro" : "Modo Oscuro"}</span>
          </button>
          <div className="mt-3 flex items-center gap-2 px-2 py-2 border-t border-border pt-3" data-testid="current-user">
            {user?.picture ? (
              <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-7 h-7 rounded-sm object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-sm bg-muted flex items-center justify-center text-xs font-bold">
                {(user?.name || user?.email || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate" data-testid="current-user-name">{user?.name || user?.email}</div>
              <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} title="Cerrar sesión"
              className="p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <div className="md:hidden flex items-center justify-between h-14 px-4 border-b border-border bg-card">
          <div className="flex items-center gap-2">
            <ChartLineUp size={20} weight="duotone" />
            <span className="font-heading font-extrabold">CASHFLOW.</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="theme-toggle-mobile-btn"
              onClick={() => setDark((d) => !d)}
              className="p-2 rounded-sm hover:bg-muted"
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button data-testid="logout-mobile-btn" onClick={logout} className="p-2 rounded-sm hover:bg-muted">
              <SignOut size={18} />
            </button>
          </div>
        </div>
        <Outlet />
      </main>

      <Toaster
        position="top-right"
        richColors
        theme={dark ? "dark" : "light"}
        toastOptions={{ style: { fontFamily: "var(--font-body)", borderRadius: "4px" } }}
      />
    </div>
  );
}
